/**
 * The plan ladder. LOCKED 27/08/2026 — do not change prices or quotas without
 * asking Lyam. Amounts here are for display and reconciliation only; the number
 * that is actually charged always comes from Stripe, resolved by lookup key.
 *
 * 08/10/2026, Lyam « NO Growth » (orchestrator/gate-go-2026-10-08.md): the ladder
 * is Free (1 campaign) and Starter (3); Multi-location stays for post-launch. The
 * retired middle tier has no entry here, so its lookup keys no longer resolve.
 */

export const INTERVALS = ['monthly', 'yearly', '3y'];

export const PLANS = {
  free: {
    key: 'free',
    name: 'Free',
    // Free is deliberately not a Stripe object. It is a workspace state.
    billable: false,
    locations: 1,
    campaigns: 1,                      // campaigns online at once (§5.5 rule 4), see campaignLimit()
    quotaPerLocation: 100,
    trial: false,
    lookupKeys: {},
    amounts: { monthly: 0, yearly: 0, '3y': 0 },
  },
  starter: {
    key: 'starter',
    name: 'Starter',
    billable: true,
    locations: 1,
    campaigns: 3,
    quotaPerLocation: 1500,
    trial: true,                       // monthly + yearly only, see trialDays()
    lookupKeys: { monthly: 'starter_monthly', yearly: 'starter_yearly', '3y': 'starter_3y' },
    amounts: { monthly: 1900, yearly: 17100, '3y': 41000 },
  },
  multi: {
    key: 'multi',
    name: 'Multi-location',
    billable: true,
    locations: 10,                     // then EXTRA_LOCATION per additional one
    campaigns: null,                   // no ceiling
    quotaPerLocation: 1500,
    trial: false,                      // no trial on the top tier
    lookupKeys: { monthly: 'multi_monthly', yearly: 'multi_yearly', '3y': 'multi_3y' },
    amounts: { monthly: 9900, yearly: 89100, '3y': 213800 },
  },
};

/**
 * Extra locations are a second subscription item carrying a quantity, not a
 * tier table — so the invoice reads "Extra location × 3" and proration works
 * when a location is added or removed mid-period. Its interval must match the
 * base item's: one subscription cannot mix intervals.
 */
export const EXTRA_LOCATION = {
  key: 'extra_location',
  name: 'Extra location',
  appliesTo: ['multi'],
  lookupKeys: {
    monthly: 'extra_location_monthly',
    yearly: 'extra_location_yearly',
    '3y': 'extra_location_3y',
  },
  amounts: { monthly: 900, yearly: 8100, '3y': 19400 },
};

export const TRIAL_DAYS = 14;

/** Quota behaviour: notify, allow a little over, then pause politely. */
export const QUOTA = {
  notifyAt: [0.8, 1.0],
  softOverage: 0.2,          // ~20 % above the allowance still goes through
  pauseAfterOverage: true,
};

export const PLAN_KEYS = Object.keys(PLANS);
export const PAID_PLAN_KEYS = PLAN_KEYS.filter((k) => PLANS[k].billable);
/**
 * What V1 sells, in the ladder's order (billing-read ruling, 10/10/2026): the merchant app's `SOLD_PLANS` means the same.
 * Multi-location stays in the ladder for the pilot row and post-launch (nine-answers § 6), but is not sold.
 */
export const SOLD_PLAN_KEYS = ['free', 'starter'];
/** What checkout takes: the billable plans V1 sells. */
export const CHECKOUT_PLAN_KEYS = SOLD_PLAN_KEYS.filter((k) => PLANS[k].billable);

export function getPlan(planKey) {
  const plan = PLANS[planKey];
  if (!plan) throw new Error(`Unknown plan: ${planKey}`);
  return plan;
}

export function isInterval(interval) {
  return INTERVALS.includes(interval);
}

/** The lookup key to hand Stripe. Never hardcode a price id anywhere. */
export function lookupKeyFor(planKey, interval) {
  const plan = getPlan(planKey);
  if (!plan.billable) return null;
  if (!isInterval(interval)) throw new Error(`Unknown interval: ${interval}`);
  return plan.lookupKeys[interval];
}

export function extraLocationLookupKey(interval) {
  if (!isInterval(interval)) throw new Error(`Unknown interval: ${interval}`);
  return EXTRA_LOCATION.lookupKeys[interval];
}

/**
 * Trial rule, locked: 14 days on Starter, monthly and yearly only.
 * Never on a three-year commitment, never on Multi-location, and never twice
 * for the same workspace.
 */
export function trialDays({ plan, interval, workspaceHasUsedTrial }) {
  if (workspaceHasUsedTrial) return 0;
  const p = getPlan(plan);
  if (!p.trial) return 0;
  if (interval !== 'monthly' && interval !== 'yearly') return 0;
  return TRIAL_DAYS;
}

/** How many Locations this workspace may run, including any extras it pays for. */
export function locationAllowance(planKey, extraLocations = 0) {
  const plan = getPlan(planKey);
  const extras = plan.key === 'multi' ? Math.max(0, extraLocations) : 0;
  return plan.locations + extras;
}

/**
 * How many campaigns a workspace may have online at once (`plan_limit`, §5.5 rule 4); null = no ceiling. The one
 * source for the API and the merchant app (FQ-I4). A stored plan the ladder no longer lists has no ceiling, as before.
 */
export function campaignLimit(planKey) {
  return PLANS[planKey]?.campaigns ?? null;
}

/**
 * The monthly quota for a workspace's stored plan. A stored plan the ladder no longer lists (0001's CHECK still
 * allows the retired tier) reads as Free's, as the v2 page does, instead of failing the customer's play.
 */
export function quotaFor(planKey) {
  return (PLANS[planKey] ?? PLANS.free).quotaPerLocation;
}

/** The safe pause: once the month holds this many participations at a Location, a new one is refused (~20 % above the quota). */
export function quotaCeiling(quota) {
  return Math.floor(quota * (1 + QUOTA.softOverage));
}

/**
 * What to do with one more interaction at a Location this month.
 * Returns { allowed, state, ratio, notify } where state is one of
 * 'ok' | 'warn' | 'over' | 'paused' and notify is 80 | 100 | null.
 */
export function quotaDecision({ used, quota }) {
  const ceiling = quotaCeiling(quota);
  const ratio = quota > 0 ? used / quota : 0;
  let notify = null;
  if (used === Math.floor(quota * 0.8)) notify = 80;
  if (used === quota) notify = 100;
  if (used >= ceiling) return { allowed: false, state: 'paused', ratio, notify };
  if (used >= quota) return { allowed: true, state: 'over', ratio, notify };
  if (ratio >= 0.8) return { allowed: true, state: 'warn', ratio, notify };
  return { allowed: true, state: 'ok', ratio, notify };
}

/**
 * The period key for monthly_usage. ONE clock (audit T1.9, 08/10/2026): `draw.js › monthBounds`, in the
 * workspace's timezone — the UTC bucket that lived here rolled the quota at a different hour than the caps.
 */
export { currentPeriod, monthBounds } from './draw.js';

/** Reverse lookup: Stripe hands us a lookup key, we need the plan it means. */
export function planFromLookupKey(lookupKey) {
  for (const plan of Object.values(PLANS)) {
    if (!plan.billable) continue;
    for (const [interval, key] of Object.entries(plan.lookupKeys)) {
      if (key === lookupKey) return { plan: plan.key, interval };
    }
  }
  for (const [interval, key] of Object.entries(EXTRA_LOCATION.lookupKeys)) {
    if (key === lookupKey) return { plan: 'extra_location', interval };
  }
  return null;
}
